import { FC, useRef, useState } from "react";

import { Button, SpacingToken, Text } from "@livechat/design-system-react-components";

import { HelpCenter } from "constants/help-center";
import { LOCAL_STORAGE_SAVED_DOMAIN_KEY } from "constants/settings";
import { useChatWidget } from "hooks/use-chat-widget";
import { getItem } from "services/local-storage";
import { IEmailDomain } from "store/entities/email-domains";
import { withVar } from "themes/helpers";

import { trackSetupModalChatWithUsClicked, trackSetupModalLearnMoreClicked } from "./analytics";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IDomainType, ISetupStepProps, SetupCNameRecords, SetupDKIMRecords, SetupDomainName } from "./steps";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { DomainAdded as DomainAddedContent } from "./steps/DomainAdded";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { SetupHelpdeskDomain, SetupHelpdeskDomainButtons } from "./steps/SetupHelpdeskDomain";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { SetupSelectDomainType } from "./steps/SetupSelectDomainType";
import { ModalBlock, StyledModal, customDomainCss, linkStyles } from "./styles";

export enum DomainSetupActionType {
  SelectType = "select-type",
  NewDomain = "new-domain",
  FinishSetup = "finish-setup",
  HelpdeskDomain = "helpdesk-domain",
  DomainAdded = "domain-added",
}

export type DomainSetupAction =
  | SelectTypeAction
  | NewDomainAction
  | FinishSetupAction
  | SetupHelpdeskDomainAction
  | DomainAddedAction;
export type SelectTypeAction = {
  type: DomainSetupActionType.SelectType;
  domainName?: string;
};
export type NewDomainAction = {
  type: DomainSetupActionType.NewDomain;
  domainName?: string;
};
export type SetupHelpdeskDomainAction = {
  type: DomainSetupActionType.HelpdeskDomain;
  domainName?: string;
};
export type FinishSetupAction = {
  type: DomainSetupActionType.FinishSetup;
  domainId: string | null;
  domainName?: string;
};
export type DomainAddedAction = {
  type: DomainSetupActionType.DomainAdded;
  customDomain: boolean;
  domainName: string;
  domainId: string;
};

interface IDomainSetupModalProps {
  onClose: () => void;
  onConfigurationStringCopy: (recordType: string, field: string) => void;
  modalAction: DomainSetupAction;
  setModalAction: React.Dispatch<React.SetStateAction<DomainSetupAction | null>>;
  licenseId: number;
  createCustomDomain: (name: string, skipRetry?: boolean) => void;
  verifyCustomDomain: (domainId: string, skipRetry?: boolean) => void;
  domains: IEmailDomain[];
  showCreateEmailDomainSuccessToast(): void;
}

enum ModalSteps {
  SelectStep = 0,
  StepOne = 1,
  StepTwo = 2,
  StepThree = 3,
  HelpdeskDomainStep = 10,
  FinishingInfoStep = 11,
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const modalStep = (modalAction: DomainSetupAction) => {
  switch (modalAction.type) {
    case DomainSetupActionType.SelectType:
      return ModalSteps.SelectStep;
    case DomainSetupActionType.HelpdeskDomain:
      return ModalSteps.HelpdeskDomainStep;
    case DomainSetupActionType.NewDomain:
      if (modalAction.domainName === undefined) {
        return ModalSteps.StepOne;
      }
      return ModalSteps.StepTwo;
    case DomainSetupActionType.FinishSetup:
      return ModalSteps.StepThree;
    case DomainSetupActionType.DomainAdded:
      return ModalSteps.FinishingInfoStep;
    default:
      throw new Error("Failed to determine modal step.");
  }
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const modalTitle = (modalAction: DomainSetupAction) => {
  if (modalAction.type === DomainSetupActionType.SelectType) {
    return "What type of domain would you like to add?";
  }
  if (modalAction.type === DomainSetupActionType.HelpdeskDomain) {
    return "Add HelpDesk domain";
  }

  return "Add custom domain";
};

const ModalContent: FC<ISetupStepProps<DomainSetupAction>> = ({ modalAction, ...props }) => {
  switch (modalStep(modalAction)) {
    case ModalSteps.SelectStep:
      return <SetupSelectDomainType modalAction={modalAction as SelectTypeAction} {...props} />;
    case ModalSteps.HelpdeskDomainStep:
      return <SetupHelpdeskDomain modalAction={modalAction as SetupHelpdeskDomainAction} {...props} />;
    case ModalSteps.StepOne:
      return <SetupDomainName modalAction={modalAction as NewDomainAction} {...props} />;
    case ModalSteps.StepTwo:
      return <SetupCNameRecords modalAction={modalAction as NewDomainAction} {...props} />;
    case ModalSteps.StepThree:
      return <SetupDKIMRecords modalAction={modalAction as FinishSetupAction} {...props} />;
    case ModalSteps.FinishingInfoStep:
      return <DomainAddedContent modalAction={modalAction as DomainAddedAction} {...props} />;
    default:
      return null;
  }
};

const ModalFooter: FC<ISetupStepProps<DomainSetupAction>> = ({ modalAction, ...props }) => {
  switch (modalStep(modalAction)) {
    case ModalSteps.HelpdeskDomainStep:
      return <SetupHelpdeskDomainButtons modalAction={modalAction as SetupHelpdeskDomainAction} {...props} />;
    case ModalSteps.SelectStep:
    case ModalSteps.FinishingInfoStep:
    case ModalSteps.StepTwo:
    case ModalSteps.StepThree:
    default:
      return null;
  }
};

const ModalInfoLabel: FC<ISetupStepProps<DomainSetupAction>> = ({ modalAction }) => {
  const { openChatWidget } = useChatWidget();
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onChatWithUsClick = () => {
    openChatWidget();
    trackSetupModalChatWithUsClicked();
  };
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onHelpCenterLinkClick = () => {
    trackSetupModalLearnMoreClicked();
  };

  switch (modalStep(modalAction)) {
    case ModalSteps.StepTwo:
    case ModalSteps.StepThree:
    case ModalSteps.FinishingInfoStep:
      return (
        <Text css={{ marginTop: withVar(SpacingToken.Spacing4) }}>
          Need help? Visit the{" "}
          <Button target="_blank" kind="link-light" href={HelpCenter.HOME} onClick={onHelpCenterLinkClick} css={linkStyles}>
            HelpDesk Help Center{" "}
          </Button>{" "}
          or{" "}
          <Button kind="link-light" onClick={onChatWithUsClick}>
            chat with us
          </Button>
          .
        </Text>
      );
    default:
      return null;
  }
};

const DomainSetupModal: FC<IDomainSetupModalProps> = ({
  modalAction,
  setModalAction,
  onClose,
  licenseId,
  createCustomDomain,
  verifyCustomDomain,
  onConfigurationStringCopy,
  domains,
  showCreateEmailDomainSuccessToast,
}) => {
  const [domainName, setDomainName] = useState(getItem(LOCAL_STORAGE_SAVED_DOMAIN_KEY) || "");
  const [domainType, setDomainType] = useState<IDomainType | null>(
    domains.length > 0 && !domains.find(d => d?.name?.endsWith(".helpdesk.com")) ? "helpdesk" : "custom"
  );

  const [initialFlow, setInitialFlow] = useState<string | null>(null);
  const manualFlowRef = useRef<boolean | null>(null);

  const partProps = {
    domainName,
    setDomainName,
    onClose,
    setModalAction,
    licenseId,
    createCustomDomain,
    verifyCustomDomain,
    domains,
    onConfigurationStringCopy,
    domainType,
    setDomainType,
    manualFlowRef,
    initialFlow,
    setInitialFlow,
    showCreateEmailDomainSuccessToast,
  };

  return (
    <StyledModal
      css={modalAction.type === DomainSetupActionType.HelpdeskDomain}
      heading={modalTitle(modalAction)}
      footer={
        modalAction.type === DomainSetupActionType.HelpdeskDomain && (
          <ModalFooter modalAction={modalAction} {...partProps} />
        )
      }
      onClose={onClose}
      style={{ width: "600px" }}
    >
      <ModalBlock
        css={modalAction.type === DomainSetupActionType.NewDomain ? customDomainCss : undefined}
        data-testid="domain-setup-modal"
      >
        <ModalContent modalAction={modalAction} {...partProps} />
        {modalAction.type === DomainSetupActionType.NewDomain && (
          <ModalFooter modalAction={modalAction} {...partProps} />
        )}
      </ModalBlock>
      <ModalInfoLabel modalAction={modalAction} {...partProps} />
    </StyledModal>
  );
};

export default DomainSetupModal;
